import { RefreshCw } from "lucide-react";
import { useState } from "react";
import { useRegisterSW } from "virtual:pwa-register/react";

import { applyPwaUpdate, startPeriodicSwUpdateCheck } from "@/pwa";

/**
 * PWA 업데이트 토스트 (지침 §8-1 autoUpdate + 관제 화면 보호).
 *
 * autoUpdate 모드의 기본 동작은 새 SW 활성화 시 즉시 페이지 리로드 — 임무 관제
 * 중 예고 없는 화면 소실은 허용 불가. onNeedReload 로 자동 리로드를 가로채
 * "새 버전 적용" 토스트를 띄우고, 사용자가 안전한 시점에 탭해서 적용한다.
 * (새 SW 는 이미 활성화된 상태 — 적용 = 리로드로 새 자산 로드)
 *
 * 위치: 하단 중앙 고정 z-50 — 모바일 하단 네비(z-40)·우하단 EmergencyActionBar 와
 * 겹치지 않게 EmergencyActionBar 와 동일한 bottom 오프셋 관례 사용.
 */
export default function PwaUpdatePrompt() {
  const [needReload, setNeedReload] = useState(false);
  useRegisterSW({
    // autoUpdate 자동 리로드 가로채기 → 토스트로 사용자에게 적용 시점 위임
    onNeedReload: () => setNeedReload(true),
    // 장시간 세션 대비 주기(1h) 업데이트 확인 — 컴포넌트는 앱 수명 내내 마운트
    // 상태이므로 interval 정리는 페이지 종료에 위임
    onRegisteredSW: (swUrl, registration) => {
      startPeriodicSwUpdateCheck(swUrl, registration);
    },
  });

  if (!needReload) return null;

  return (
    <div
      role="status"
      data-testid="pwa-update-prompt"
      className={[
        "fixed z-50 left-1/2 -translate-x-1/2",
        "bottom-[calc(6rem+env(safe-area-inset-bottom))] md:bottom-6",
        "flex items-center gap-3 rounded-lg border border-border bg-surface-raised",
        "px-4 py-2 shadow-lg",
      ].join(" ")}
    >
      <RefreshCw size={18} aria-hidden="true" className="shrink-0 text-status-info" />
      <span className="text-sm text-fg-primary">새 버전이 준비되었습니다</span>
      <button
        type="button"
        onClick={() => applyPwaUpdate()}
        className={[
          "min-h-touch min-w-touch px-4 rounded-md font-semibold text-sm",
          "bg-fg-primary text-surface hover:opacity-90",
        ].join(" ")}
      >
        새 버전 적용
      </button>
      <button
        type="button"
        onClick={() => setNeedReload(false)}
        className={[
          "min-h-touch min-w-touch px-3 rounded-md text-sm",
          "border border-border text-fg-secondary hover:bg-surface",
        ].join(" ")}
      >
        나중에
      </button>
    </div>
  );
}
